import { ErrorModal, HelmetDexteel } from "@dexteel/mesf-core";
import { Box, Grid, Typography } from "@mui/material";
import { get } from "lodash-es";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { HeaderHeatDataReport } from "./components/HeaderHeatDataReport";
import { HeatReportTabs } from "./components/tabs/HeatReportTabs";
import { useHeatReportContext } from "./context/HeatReportContext";
import {
  EAFProcessData,
  HeatData,
  LMFProcessData,
} from "./models/HeatReportModels";
import {
  getCASProcessReport,
  getEAFDelays,
  getEAFProcessReport,
  getHeatReport,
  getLabReport,
  getLMFProcessReport,
  getTorchesReport,
} from "./repository/HeatReportRepository";

export const HeatReportPage = () => {
  const navigate = useNavigate();
  const { asset } = useParams<{ asset: string }>();
  const [searchParams] = useSearchParams();
  const processIdFromUrl = searchParams.get("processId");
  const treatmentNumberFromUrl = searchParams.get("treatmentNumber");
  const heatIdFromUrl = searchParams.get("heatId");
  const {
    state: { heat, treatments },
    actions: {
      setHeat,
      setEAFProcessData,
      setCASProcessData,
      setLMFProcessData,
      setTorchesData,
      setProbes,
      setAdditions,
      setCharges,
      setHeatings,
      setPhases,
      setInjections,
      setStrandData,
      setStrandSpeeds,
      setMoldLevels,
      setElementData,
      setIsDataLoading,
      setDelays,
      setTorches,
      setGroupedLength,
      setBilletsYard,
      setTreatments,
    },
  } = useHeatReportContext();

  const [error, setError] = useState<string>("");
  const [currentLMFProcess, setCurrentLMFProcess] = useState<{
    processId: number | null;
    treatmentNumber: number | null;
  }>({ processId: null, treatmentNumber: null });

  const getDataFromAPI = async (
    heatId: number | null,
    updateUrl: boolean = false,
  ) => {
    setIsDataLoading(true);
    try {
      if (updateUrl && heatId) {
        const params = new URLSearchParams(searchParams);
        params.set("heatId", String(heatId));
        navigate({ search: params.toString() });
      }
      const heatResp = await getHeatReport({ HeatId: heatId });

      if (heatResp.ok) {
        const heatData: HeatData = get(heatResp.data, "tables[0].rows[0]", {});
        setHeat(heatData);

        const treatmentsData: Array<{
          ProcessId: number;
          TreatmentNumber: number;
        }> = get(heatResp.data, "tables[1].rows", []);

        setTreatments(treatmentsData);

        if (treatmentsData.length > 0) {
          setCurrentLMFProcess({
            processId: treatmentsData[0].ProcessId,
            treatmentNumber: treatmentsData[0].TreatmentNumber,
          });
        } else if (heatData.LMFProcessId) {
          setCurrentLMFProcess({
            processId: heatData.LMFProcessId,
            treatmentNumber: 1,
          });
        }
      } else {
        setError(heatResp.message);
      }
    } catch (err) {
      setError("Error fetching heat data");
      console.error(err);
    } finally {
      setIsDataLoading(false);
    }
  };

  const getProcessDataFromAPI = async () => {
    setIsDataLoading(true);

    try {
      if (asset === "eaf") {
        const processId = processIdFromUrl
          ? parseInt(processIdFromUrl)
          : heat.EAFProcessId;
        if (processId) {
          const resp = await getEAFProcessReport({
            ProcessId: processId,
          });

          if (resp.ok) {
            const processData: EAFProcessData = get(
              resp.data,
              "tables[0].rows[0]",
              {},
            );
            setEAFProcessData(processData);
            setProbes(get(resp.data, "tables[1].rows", []));
            setCharges(get(resp.data, "tables[2].rows", []));
            setAdditions(get(resp.data, "tables[3].rows", []));
            setPhases(get(resp.data, "tables[4].rows", []));
            setInjections(get(resp.data, "tables[7].rows", []));
            setHeatings(get(resp.data, "tables[8].rows", []));

            const delaysResp = await getEAFDelays(processId);
            if (delaysResp.ok) {
              setDelays(get(delaysResp.data, "tables[0].rows", []));
            }
          }
        }
      } else if (asset?.startsWith("lmf")) {
        let targetProcessId = processIdFromUrl
          ? parseInt(processIdFromUrl)
          : currentLMFProcess.processId;
        let targetTreatmentNumber = currentLMFProcess.treatmentNumber;

        const treatmentNumFromUrl = treatmentNumberFromUrl
          ? parseInt(treatmentNumberFromUrl)
          : asset.includes("-")
            ? parseInt(asset.split("-")[1])
            : null;

        if (treatmentNumFromUrl) {
          const treatment = treatments.find(
            (t) => t.TreatmentNumber === treatmentNumFromUrl,
          );
          if (treatment) {
            targetProcessId = treatment.ProcessId;
            targetTreatmentNumber = treatment.TreatmentNumber;
          }
        }

        if (targetProcessId) {
          const resp = await getLMFProcessReport({
            ProcessId: targetProcessId,
          });

          if (resp?.ok) {
            const processData: LMFProcessData = {
              ...get(resp.data, "tables[0].rows[0]", {}),
              ProcessId: targetProcessId,
              TreatmentNumber: targetTreatmentNumber || 1,
            };

            setLMFProcessData(processData);
            setProbes(get(resp.data, "tables[1].rows", []));
            setHeatings(get(resp.data, "tables[2].rows", []));
            setAdditions(get(resp.data, "tables[3].rows", []));

            setCurrentLMFProcess({
              processId: targetProcessId,
              treatmentNumber: targetTreatmentNumber,
            });
          }
        }
      } else if (asset === "caster") {
        const processId = processIdFromUrl
          ? parseInt(processIdFromUrl)
          : heat.CASProcessId;
        if (processId) {
          const resp = await getCASProcessReport({
            ProcessId: processId,
          });

          if (resp?.ok) {
            setCASProcessData(get(resp.data, "tables[0].rows[0]", {}));
            setStrandData(get(resp.data, "tables[1].rows", []));
            setProbes(get(resp.data, "tables[2].rows", []));
            setStrandSpeeds(get(resp.data, "tables[4].rows", {}));
            setMoldLevels(get(resp.data, "tables[5].rows", {}));

            const torchesResp = await getTorchesReport({
              ProcessId: processId,
            });

            if (torchesResp?.ok) {
              setTorchesData(get(torchesResp.data, "tables[0].rows[0]", {}));
              setTorches(get(torchesResp.data, "tables[1].rows", []));
              setGroupedLength(get(torchesResp.data, "tables[2].rows", []));
              setBilletsYard(get(torchesResp.data, "tables[3].rows", []));
            }
          }
        }
      } else if (asset === "lab") {
        const processId = processIdFromUrl
          ? parseInt(processIdFromUrl)
          : heat.LMFProcessId;
        if (processId) {
          const resp = await getLabReport({
            ProcessId: processId,
          });

          if (resp?.ok) {
            setElementData(get(resp.data, "tables[0].rows[0]", {}));
          }
        }
      }
    } catch (err) {
      setError("Error fetching process data");
      console.error(err);
    } finally {
      setIsDataLoading(false);
    }
  };

  const handleNavigationChange = async (heatId: number | null) => {
    if (heatId === null) {
      setIsDataLoading(true);
      try {
        const heatResp = await getHeatReport({ HeatId: null });
        if (heatResp.ok) {
          const heatData: HeatData = get(
            heatResp.data,
            "tables[0].rows[0]",
            {},
          );
          const treatmentsData: Array<{
            ProcessId: number;
            TreatmentNumber: number | null;
          }> = get(heatResp.data, "tables[1].rows", []);

          setHeat(heatData);
          setTreatments(treatmentsData);

          const params = new URLSearchParams(searchParams);
          params.set("heatId", String(heatData.HeatId));

          let processId = null;
          let treatmentNumber = null;

          if (asset?.startsWith("lmf")) {
            if (treatmentsData.length > 0) {
              const lastTreatment = treatmentsData.reduce((prev, curr) =>
                (prev.TreatmentNumber ?? 0) > (curr.TreatmentNumber ?? 0)
                  ? prev
                  : curr,
              );
              processId = lastTreatment.ProcessId;
              treatmentNumber = lastTreatment.TreatmentNumber;
            } else if (heatData.LMFProcessId) {
              processId = heatData.LMFProcessId;
              treatmentNumber = 1;
            }
          } else if (asset === "eaf") {
            processId = heatData.EAFProcessId;
          } else if (asset === "caster") {
            processId = heatData.CASProcessId;
          }

          if (processId) {
            params.set("processId", String(processId));
          }
          if (asset?.startsWith("lmf") && treatmentNumber) {
            params.set("treatmentNumber", String(treatmentNumber));
          } else {
            params.delete("treatmentNumber");
          }

          navigate({ search: params.toString() }, { replace: true });
        } else {
          setError(heatResp.message);
        }
      } catch (err) {
        setError("Error fetching last heat data");
        console.error(err);
      } finally {
        setIsDataLoading(false);
      }
    } else {
      const params = new URLSearchParams(searchParams);
      params.set("heatId", String(heatId));
      params.delete("processId");
      params.delete("treatmentNumber");
      navigate({ search: params.toString() });
    }
  };

  useEffect(() => {
    const heatId = heatIdFromUrl ? parseInt(heatIdFromUrl) : null;
    if (heatIdFromUrl === null) {
      handleNavigationChange(null);
    } else if (heatId) {
      getDataFromAPI(heatId);
    }
  }, [asset, heatIdFromUrl]);

  useEffect(() => {
    if (!heat || !heat.HeatId || !treatments) return;
    if (String(heat.HeatId) !== String(heatIdFromUrl)) return;

    const params = new URLSearchParams(searchParams);
    let needsUpdate = false;

    let newProcessId: number | null = null;
    let newTreatmentNumber: number | null = null;

    if (asset?.startsWith("lmf")) {
      if (treatments.length === 0 && !heat.LMFProcessId) return;

      const urlTreatmentNumber = treatmentNumberFromUrl
        ? parseInt(treatmentNumberFromUrl)
        : null;
      const treatment = urlTreatmentNumber
        ? treatments.find((t) => t.TreatmentNumber === urlTreatmentNumber)
        : null;

      if (treatment) {
        newProcessId = treatment.ProcessId;
        newTreatmentNumber = treatment.TreatmentNumber;
      } else if (treatments.length > 0) {
        newProcessId = treatments[0].ProcessId;
        newTreatmentNumber = treatments[0].TreatmentNumber;
      } else if (heat.LMFProcessId) {
        newProcessId = heat.LMFProcessId;
        newTreatmentNumber = 1;
      }
    } else if (asset === "eaf") {
      newProcessId = heat.EAFProcessId;
    } else if (asset === "caster") {
      newProcessId = heat.CASProcessId;
    }

    if (newProcessId && String(newProcessId) !== params.get("processId")) {
      params.set("processId", String(newProcessId));
      needsUpdate = true;
    }

    if (asset?.startsWith("lmf") && newTreatmentNumber) {
      if (String(newTreatmentNumber) !== params.get("treatmentNumber")) {
        params.set("treatmentNumber", String(newTreatmentNumber));
        needsUpdate = true;
      }
    } else if (params.has("treatmentNumber")) {
      params.delete("treatmentNumber");
      needsUpdate = true;
    }

    if (needsUpdate) {
      navigate({ search: params.toString() }, { replace: true });
    }
  }, [
    asset,
    heat,
    treatments,
    heatIdFromUrl,
    treatmentNumberFromUrl,
    searchParams,
    navigate,
  ]);

  useEffect(() => {
    if (processIdFromUrl) {
      getProcessDataFromAPI();
    }
  }, [asset, processIdFromUrl, treatmentNumberFromUrl]);

  return (
    <>
      <HelmetDexteel
        title={`${heat?.HeatName ? `${heat.HeatName} - ` : ""}Heat Report`}
      />
      <Grid item xs={12}>
        <Box
          sx={(theme) => ({
            backgroundColor: theme.palette.grey[100],
            border: `1px solid ${theme.palette.divider}`,
            p: 2,
          })}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Heat Report
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <HeaderHeatDataReport
                key={`header-heat-data-report-${heat.HeatId}`}
                heat={heat}
                getDataFromAPI={handleNavigationChange}
              />
            </Grid>
            <Grid
              item
              xs={12}
              container
              justifyContent="center"
              alignContent="center"
            >
              <HeatReportTabs />
            </Grid>
          </Grid>
        </Box>
      </Grid>
      <ErrorModal error={error} onHide={() => setError("")} />
    </>
  );
};
